import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
    Lock,
    Eye,
    EyeOff,
    AtSign,
    ArrowRight,
    Mail,
    Loader2,
    X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// New logo image URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/mnjq1cak_Recovery%20Room%20Archive-07.png";

const MemberLoginPage = () => {
    const navigate = useNavigate();
    const { loginMember } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    
    // Forgot password modal state
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [resetSent, setResetSent] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (!resetEmail.trim()) {
            toast.error('Please enter your email address');
            return;
        }
        
        setIsResetting(true);
        try {
            const response = await fetch(`${API_URL}/api/auth/member/reset-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail })
            });
            
            if (response.ok) {
                setResetSent(true);
                toast.success('Password reset link sent! Check your email.');
            } else {
                toast.error('Unable to process request. Please try again.');
            }
        } catch (error) {
            toast.error('An error occurred. Please try again.');
        } finally {
            setIsResetting(false);
        }
    };

    const closeForgotModal = () => {
        setShowForgotModal(false);
        setResetEmail('');
        setResetSent(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.username.trim()) {
            toast.error('Please enter your username');
            return;
        }
        if (!formData.password) {
            toast.error('Please enter your password');
            return;
        }

        setIsLoading(true);
        try {
            await loginMember(formData.username, formData.password);
            toast.success('Welcome back!');
            navigate('/member/dashboard');
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (detail === 'Account is deactivated') {
                toast.error('Your account has been deactivated. Please contact an administrator.');
            } else {
                toast.error('Invalid username or password');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#3D4435] flex flex-col">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#3D4435]/90 backdrop-blur-lg border-b border-[#5A6350]">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/join" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Recovery Room Logo" className="w-10 h-10 object-contain" />
                        <div>
                            <h1 className="text-xl font-bold text-white font-['Playfair_Display']">Recovery Room</h1>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link 
                            to="/login" 
                            className="text-sm text-[#666] hover:text-[#A0A0A0] font-medium"
                            data-testid="admin-login-link"
                        >
                            Admin Login
                        </Link>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-white font-['Playfair_Display'] mb-2">
                            Welcome Back, Brother
                        </h2>
                        <p className="text-[#A0A0A0]">
                            Sign in to your member account to connect with the community.
                        </p>
                    </div>

                    <Card className="bg-[#4A5240] border-[#5A6350] shadow-2xl overflow-hidden">
                        <div className="h-1 bg-gradient-to-r from-[#C9A227] to-[#E8D48A]" />
                        <CardContent className="p-6">
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="username" className="text-white flex items-center gap-2">
                                        <AtSign className="w-4 h-4 text-[#C9A227]" />
                                        Username
                                    </Label>
                                    <Input
                                        id="username"
                                        value={formData.username}
                                        onChange={(e) => handleInputChange('username', e.target.value)}
                                        placeholder="Enter your username"
                                        className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                        data-testid="member-login-username-input"
                                        autoComplete="username"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-white flex items-center gap-2">
                                        <Lock className="w-4 h-4 text-[#C9A227]" />
                                        Password
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={(e) => handleInputChange('password', e.target.value)}
                                            placeholder="Enter your password"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227] pr-10"
                                            data-testid="member-login-password-input"
                                            autoComplete="current-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <div className="text-right">
                                        <button
                                            type="button"
                                            onClick={() => setShowForgotModal(true)}
                                            className="text-[#C9A227] hover:text-[#E8D48A] text-sm font-medium transition-colors"
                                            data-testid="forgot-password-link"
                                        >
                                            Forgot Password?
                                        </button>
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                    data-testid="member-login-submit-btn"
                                >
                                    {isLoading ? 'Signing in...' : 'Sign In'}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </form>

                            <div className="mt-6 pt-6 border-t border-[#5A6350]">
                                <p className="text-center text-[#A0A0A0] text-sm">
                                    Not a member yet?{' '}
                                    <Link 
                                        to="/member/register" 
                                        className="text-[#C9A227] hover:text-[#E8D48A] font-medium"
                                        data-testid="register-link"
                                    >
                                        Create an account
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Forgot Password Modal */}
            {showForgotModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="forgot-password-modal">
                    <Card className="bg-[#4A5240] border-[#5A6350] shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="h-1 bg-gradient-to-r from-[#C9A227] to-[#E8D48A]" />
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-white font-['Playfair_Display']">
                                    Reset Password
                                </h3>
                                <button
                                    onClick={closeForgotModal}
                                    className="text-[#666] hover:text-white transition-colors"
                                    data-testid="close-forgot-modal"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {resetSent ? (
                                <div className="text-center py-4">
                                    <div className="w-16 h-16 bg-[#C9A227]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Mail className="w-8 h-8 text-[#C9A227]" />
                                    </div>
                                    <h4 className="text-white font-semibold mb-2">Check Your Email</h4>
                                    <p className="text-[#A0A0A0] text-sm mb-4">
                                        If an account with that email exists, we've sent you a password reset link. 
                                        The link will expire in 1 hour.
                                    </p>
                                    <Button
                                        onClick={closeForgotModal}
                                        className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                        data-testid="close-reset-sent"
                                    >
                                        Back to Login
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleForgotPassword} className="space-y-4">
                                    <p className="text-[#A0A0A0] text-sm">
                                        Enter the email address associated with your account and we'll send you a link to reset your password.
                                    </p>
                                    <div className="space-y-2">
                                        <Label htmlFor="reset-email" className="text-white flex items-center gap-2">
                                            <Mail className="w-4 h-4 text-[#C9A227]" />
                                            Email Address
                                        </Label>
                                        <Input
                                            id="reset-email"
                                            type="email"
                                            value={resetEmail}
                                            onChange={(e) => setResetEmail(e.target.value)}
                                            placeholder="Enter your email"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="reset-email-input"
                                            autoComplete="email"
                                        />
                                    </div>
                                    <div className="flex gap-3 pt-2">
                                        <Button
                                            type="button"
                                            onClick={closeForgotModal}
                                            variant="outline"
                                            className="flex-1 border-[#5A6350] text-white hover:bg-[#3D4435]"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            disabled={isResetting}
                                            className="flex-1 bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                            data-testid="send-reset-link-btn"
                                        >
                                            {isResetting ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                'Send Reset Link'
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default MemberLoginPage;
